import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import { WEB_COMPONENT_GRID_VIEW, WEB_COMPONENT_GZ_TITLE } from '../../../webcomponents.constants';
import { CompaniesGridDataProvider } from '../dataProvider/CompaniesGridDataProvider';
import {
  ColumnIndexes,
  ColumnKeys,
  DEFAULT_COLUMN_CONFIGS,
  DEFAULT_VALUE_FOR_FILTERS_VISIBILITY,
  DEFAULT_VISIBLE_COLUMNS,
  FILTERS,
  getColumnDetails,
  PREDEFINED_FILTERS_KEYS,
} from './gridConfig/companies-grid-columns';
import {
  renderAutoRenewal,
  renderCompanyLicenseKey,
  renderCompanyName,
  renderCompanyProductName,
  renderCompanyProductStatus,
  renderCompanyProductType,
  renderEndSubscriptionDate,
  renderMinimumUsage,
  renderMspInProductTrialStatus,
  renderProductsExpiryDate,
  renderTotalSeats,
  renderUsageBreakdown,
} from './customGridRenderers';
import { CompanyTypeData } from '../../../company/types/company-type-data';
import { DEFAULT_COMPACT_VIEW, PAGE_SIZE_OPTIONS } from './gridConfig/companies-grid-page';
import { OnCompanyNameClick } from '../events/events';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GridTitleConfig } from '../../../grid/uiElements/gridViewTitle/types';
import { Company } from '../types/companies-grid-company';
import { DEFAULT_PAGE_SIZE, SelectionStoreMode, ViewType } from '../../../grid/gridView/types';
import { SelectOption } from '../../../ui/gzSelect/types';
import { CompaniesItemsActions } from '../types/companies-items-actions';
import { IntegrationType } from '../../../company/types/IntegrationType';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { GridElement, GridItemModel } from '@vaadin/vaadin-grid/vaadin-grid';
import { html, render } from 'lit';
import { getColumns, getRenderers } from './gridConfig/products-grid-columns';
import _ from 'lodash';
import { Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';

export class CompaniesGridView extends BaseWebComponentView {
  public companyTypes: CompanyTypeData[];

  public $activateCompanyAction: SelectOption;
  public $suspendCompanyAction: SelectOption;
  public $revokeMspInProductTrialsForMdr: SelectOption;
  public $allowMspInProductTrialsForMdr: SelectOption;
  public $revokeMspInProductTrialsForEdr: SelectOption;
  public $allowMspInProductTrialsForEdr: SelectOption;
  public $revokeMspInProductTrialsVisibilityForMdr: SelectOption;
  public $allowMspInProductTrialsVisibilityForMdr: SelectOption;
  public $revokeMspInProductTrialsVisibilityForEdr: SelectOption;
  public $allowMspInProductTrialsVisibilityForEdr: SelectOption;
  public $importCompanyFromConnectWiseAction: SelectOption;

  public dom: {
    $wrapper: HTMLElement;
    $grid: GridViewWebComponent<ColumnIndexes, ColumnKeys, Company>;

    $addCompanyButton: GzButtonWebComponent;
    $deleteCompanyButton: GzButtonWebComponent;

    companyActions: {
      $companyActionsButton: GzSelectWebComponent;
      $companyActionsText: HTMLElement;
    };
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $grid: this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW) as GridViewWebComponent<ColumnIndexes, ColumnKeys>,

      $addCompanyButton: this.shadow.querySelector('#add-company-button'),
      $deleteCompanyButton: this.shadow.querySelector('#delete-company-button'),

      companyActions: {
        $companyActionsButton: this.shadow.querySelector('#company-actions-split-button'),
        $companyActionsText: this.shadow.querySelector('#company-actions-split-button-text'),
      },
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$addCompanyButton.innerText = this.dependencies.translateFn('Add company');
    this.dom.$deleteCompanyButton.innerText = this.dependencies.translateFn('Delete');
    this.dom.companyActions.$companyActionsText.innerText = this.dependencies.translateFn('More actions');
  }

  buildSuccessView({ companyTypes }: { companyTypes: CompanyTypeData[] }) {
    this.companyTypes = companyTypes;

    this.buildGridView().then().catch(console.error);
    this.buildCompanyActionsSelectList();
  }

  private async buildGridView() {
    const allColumnDetails = getColumnDetails(this.dependencies.translateFn, this.companyTypes);
    this.dom.$grid.propertiesSet = false;

    this.dom.$grid.dataProvider = new CompaniesGridDataProvider(this.dependencies, allColumnDetails);

    const dataProvider = this.dom.$grid.dataProvider as CompaniesGridDataProvider;
    const gridState = dataProvider.getViewState();

    this.dom.$grid.viewId = 'companies-grid-default-view';

    this.dom.$grid.isCompactViewChecked = gridState.compactView ?? DEFAULT_COMPACT_VIEW;
    this.dom.$grid.hasPagination = true;
    this.dom.$grid.pageSizeOptions = PAGE_SIZE_OPTIONS;
    this.dom.$grid.pageSize = gridState.pageSize ?? DEFAULT_PAGE_SIZE;
    this.dom.$grid.hasSelectableRows = true;

    const gridSidePanelWrapper = document
      .querySelector('gz-companies-app')
      ?.shadowRoot?.querySelector('#grid-side-panel-wrapper');
    this.dom.$grid.sideMenuParent = gridSidePanelWrapper ? gridSidePanelWrapper : this.shadow;

    this.dom.$grid.enableCSVExport = true;
    this.dom.$grid.rowDetailsRenderer = this.rowDetailsRenderer.bind(this);

    this.dom.$grid.allColumnDetails = allColumnDetails;
    this.dom.$grid.columns = gridState.columns ?? DEFAULT_VISIBLE_COLUMNS;
    this.dom.$grid.columnConfigs = gridState.columnConfigs ?? _.cloneDeep(DEFAULT_COLUMN_CONFIGS);
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.COMPANY_NAME]: renderCompanyName.bind(null, this.dispatchCompanyNameClickedEvent.bind(this)),
      [ColumnKeys.PRODUCT_NAME]: renderCompanyProductName.bind(this),
      [ColumnKeys.PRODUCT_TYPE]: renderCompanyProductType.bind(this),
      [ColumnKeys.PRODUCT_STATUS]: renderCompanyProductStatus.bind(this),
      [ColumnKeys.LICENSE_KEY]: renderCompanyLicenseKey.bind(this),
      [ColumnKeys.EXPIRY_DATE]: renderProductsExpiryDate.bind(this),
      [ColumnKeys.TOTAL_SEATS]: renderTotalSeats.bind(this),
      [ColumnKeys.USAGE_BREAKDOWN]: renderUsageBreakdown.bind(this),
      [ColumnKeys.AUTO_RENEWAL]: renderAutoRenewal.bind(this),
      [ColumnKeys.SUBSCRIPTION_END_DATE]: renderEndSubscriptionDate.bind(this),
      [ColumnKeys.MINIMUM_USAGE]: renderMinimumUsage.bind(this),
      [ColumnKeys.MSP_IN_PRODUCT_TRIAL_STATUS]: renderMspInProductTrialStatus.bind(this),
    };

    this.dom.$grid.showFilters = gridState.showFilters ?? DEFAULT_VALUE_FOR_FILTERS_VISIBILITY;

    this.dom.$grid.defaultViewProps = {
      id: 'companies-item-grid-default-view',
      name: 'Default view',
      type: ViewType.DEFAULT,
      displayedColumns: DEFAULT_VISIBLE_COLUMNS,
      filters: FILTERS,
      pageSize: DEFAULT_PAGE_SIZE,
      compactView: false,
      columnConfigs: DEFAULT_COLUMN_CONFIGS,
    };

    this.dom.$grid.selectionStoreConfig = {
      functionalMode: SelectionStoreMode.RECORDS,
    };

    this.dom.$grid.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('No companies'),
    };

    this.dom.$grid.predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
    this.dom.$grid.filters = gridState.filters ?? FILTERS;

    this.dom.$grid.propertiesSet = true;

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().then().catch(console.error);
    }
  }

  private buildCompanyActionsSelectList() {
    const isConnectWiseIntegrationAvailable =
      this.dependencies.IntegrationsLib.self.hasCloudIntegrationVisible(IntegrationType.Connectwise) &&
      this.dependencies.IntegrationsLib.self.hasCloudIntegrationConfigured(IntegrationType.Connectwise);

    this.$activateCompanyAction = {
      title: this.dependencies.translateFn('Activate'),
      value: CompaniesItemsActions.ActivateCompany,
      disabled: true,
    };
    this.$suspendCompanyAction = {
      title: this.dependencies.translateFn('Suspend'),
      value: CompaniesItemsActions.SuspendCompany,
      disabled: true,
    };
    this.$revokeMspInProductTrialsForMdr = {
      title: 'Revoke MSP MDR Product Trials',
      value: CompaniesItemsActions.RevokeMSPInProductTrialsForMdr,
      disabled: true,
    };
    this.$allowMspInProductTrialsForMdr = {
      title: 'Allow MSP MDR Product Trials',
      value: CompaniesItemsActions.AllowMSPInProductTrialsForMdr,
      disabled: true,
    };
    this.$allowMspInProductTrialsForEdr = {
      title: 'Allow MSP EDR Product Trials',
      value: CompaniesItemsActions.AllowMSPInProductTrialsForEdr,
      disabled: true,
    };
    this.$revokeMspInProductTrialsForEdr = {
      title: 'Revoke MSP EDR Product Trials',
      value: CompaniesItemsActions.RevokeMSPInProductTrialsForEdr,
      disabled: true,
    };
    this.$revokeMspInProductTrialsVisibilityForMdr = {
      title: 'Revoke MSP MDR Product Trials Visibility',
      value: CompaniesItemsActions.RevokeMSPInProductTrialsVisibilityForMdr,
      disabled: true,
    };
    this.$allowMspInProductTrialsVisibilityForMdr = {
      title: 'Allow MSP MDR Product Trials Visibility',
      value: CompaniesItemsActions.AllowMSPInProductTrialsVisibilityForMdr,
      disabled: true,
    };
    this.$allowMspInProductTrialsVisibilityForEdr = {
      title: 'Allow MSP EDR Product Trials Visibility',
      value: CompaniesItemsActions.AllowMSPInProductTrialsVisibilityForEdr,
      disabled: true,
    };
    this.$revokeMspInProductTrialsVisibilityForEdr = {
      title: 'Revoke MSP EDR Product Trials Visibility',
      value: CompaniesItemsActions.RevokeMSPInProductTrialsVisibilityForEdr,
      disabled: true,
    };

    this.dom.companyActions.$companyActionsButton.options = [this.$activateCompanyAction, this.$suspendCompanyAction];

    if (Application.Main.hasAllRights([Rights.RIGHT_MANAGE_MSP_TRIAL_FEATURES])) {
      this.dom.companyActions.$companyActionsButton.options.push(
        this.$allowMspInProductTrialsForMdr,
        this.$revokeMspInProductTrialsForMdr,
        this.$allowMspInProductTrialsForEdr,
        this.$revokeMspInProductTrialsForEdr,
        this.$allowMspInProductTrialsVisibilityForMdr,
        this.$revokeMspInProductTrialsVisibilityForMdr,
        this.$allowMspInProductTrialsVisibilityForEdr,
        this.$revokeMspInProductTrialsVisibilityForEdr
      );
    }

    if (isConnectWiseIntegrationAvailable) {
      this.$importCompanyFromConnectWiseAction = {
        title: this.dependencies.translateFn('Import from ConnectWise'),
        value: CompaniesItemsActions.ImportCompanyFromConnectWise,
      };

      this.dom.companyActions.$companyActionsButton.options.push(this.$importCompanyFromConnectWiseAction);
    }
  }

  private rowDetailsRenderer(root: HTMLElement, gridElement: GridElement, model: GridItemModel) {
    const company = model.item as Company;

    const items =
      company.licenses?.map(license => {
        return _.merge(license, { subscription: company.subscription });
      }) ?? [];

    const columns = getColumns(this.dependencies.translateFn);
    const renderers = getRenderers(this.dependencies.translateFn, this.dependencies.getTimezone());

    render(
      html`
        <div class="d-flex justify-end">
          <gz-light-grid .items="${items}" .columns="${columns}" .columnRenderers="${renderers}"></gz-light-grid>
        </div>
      `,
      root
    );
  }

  private dispatchCompanyNameClickedEvent(companyId: string, companyName: string) {
    this.dom.$grid.dispatchEvent(new OnCompanyNameClick(companyId, companyName));
  }
}
